import { useState } from "react";
import {
    Switch,
    type SwitchProps,
    type SwitchSize,
} from "@design-system/Switch/Switch";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { useAnatomyMode } from "./componentAnatomyMode";
import { BooleanControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import {
    SwitchIndicators,
    SwitchLabelExamples,
    SwitchMatrix,
} from "./SwitchPlaygroundMatrix";
import styles from "./DatePickerPlayground.module.scss";

type LabelPlacement = NonNullable<SwitchProps["labelPlacement"]>;

const switchSizes = ["md", "sm"] as const satisfies readonly SwitchSize[];

const labelPlacements = [
    "end",
    "start",
] as const satisfies readonly LabelPlacement[];

const sandboxLabel = "Notify me";

const anatomyVariantId = ({
    isChecked,
    isLoading,
}: {
    isChecked: boolean;
    isLoading: boolean;
}) => {
    const value = isChecked ? "on" : "off";
    return isLoading ? `${value}-loading` : value;
};

export const SwitchPlayground = () => {
    const [isChecked, setIsChecked] = useState(false);
    const [size, setSize] = useState<SwitchSize>("md");
    const [labelPlacement, setLabelPlacement] = useState<LabelPlacement>("end");
    const [isDisabled, setIsDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [hasLabel, setHasLabel] = useState(true);
    const [isLabelHidden, setIsLabelHidden] = useState(false);

    const isLabelShown = hasLabel && !isLabelHidden;
    const anatomy = useAnatomyMode({
        component: "switch",
        variantId: anatomyVariantId({ isChecked, isLoading }),
        sizeId: size,
        isDisabled,
        isReadOnly,
        isInvalid,
        switchContent: {
            label: isLabelShown ? sandboxLabel : null,
            labelPlacement,
        },
    });

    const liveSummary = `${size} · ${isChecked ? "on" : "off"} · label ${labelPlacement}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Size</h2>
                        <ToggleGroup.Root
                            value={size}
                            onValueChange={(value) => {
                                if (value) {
                                    setSize(value);
                                }
                            }}
                        >
                            {switchSizes.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                        <h2 className={styles.subheading}>Label placement</h2>
                        <ToggleGroup.Root
                            value={labelPlacement}
                            onValueChange={(value) => {
                                if (value) {
                                    setLabelPlacement(value);
                                }
                            }}
                        >
                            {labelPlacements.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Checked"
                                checked={isChecked}
                                onChange={setIsChecked}
                            />
                            <BooleanControl
                                label="Disabled"
                                checked={isDisabled}
                                onChange={setIsDisabled}
                            />
                            <BooleanControl
                                label="Loading"
                                checked={isLoading}
                                onChange={setIsLoading}
                            />
                            <BooleanControl
                                label="Invalid"
                                checked={isInvalid}
                                onChange={setIsInvalid}
                            />
                            <BooleanControl
                                label="Read-only"
                                checked={isReadOnly}
                                onChange={setIsReadOnly}
                            />
                            <BooleanControl
                                label="Label"
                                checked={hasLabel}
                                onChange={setHasLabel}
                            />
                            <BooleanControl
                                label="Label hidden"
                                checked={isLabelHidden}
                                disabled={!hasLabel}
                                onChange={setIsLabelHidden}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={
                    anatomy.isActive ? `x-ray · ${liveSummary}` : liveSummary
                }
                belowStage={
                    <>
                        <SwitchMatrix />
                        <SwitchLabelExamples />
                        <SwitchIndicators />
                    </>
                }
                output={JSON.stringify(
                    {
                        checked: isChecked,
                        size,
                        labelPlacement,
                        disabled: isDisabled,
                        loading: isLoading,
                        invalid: isInvalid,
                        readOnly: isReadOnly,
                        label: hasLabel ? sandboxLabel : null,
                        labelHidden: isLabelHidden,
                        forcedState: anatomy.forcedState ?? null,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <Switch
                        checked={isChecked}
                        onCheckedChange={setIsChecked}
                        size={size}
                        labelPlacement={labelPlacement}
                        disabled={isDisabled}
                        loading={isLoading}
                        invalid={isInvalid}
                        readOnly={isReadOnly}
                        label={hasLabel ? sandboxLabel : undefined}
                        labelHidden={isLabelHidden}
                        aria-label={hasLabel ? undefined : sandboxLabel}
                        data-forced-state={anatomy.forcedState}
                    />
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
